import React, {useMemo} from 'react'
import {getFormattedRelativeDate, pluralize} from '@therms/web-js'
import {EntryListItem} from './EntryListItem'
import {DateTime, RPCDocs} from '@therms/react-modules'

interface HeaderTimeLabelProps {
  entries: RPCDocs.Locations.GetLocationLogEntryList.v2.Entry[]
}

function HeaderTimeLabel({entries}: HeaderTimeLabelProps) {
  const [startTimestamp, endTimestamp] = useMemo(() => {
    if (entries.length === 1) {
      return [
        entries[0].timestamp,
        entries[0].timestampEnd || entries[0].timestamp,
      ]
    }

    let start: string = entries[0].timestamp
    let end: string =
      entries.at(-1)?.timestampEnd || entries.at(-1)?.timestamp || start

    entries.forEach((entry) => {
      if (entry.timestamp < start) {
        start = entry.timestamp
      }

      if (entry.timestampEnd && entry.timestampEnd > end) {
        end = entry.timestampEnd
      }
    })

    return [start, end]
  }, [entries])

  return (
    <span className="text-sm">
      <span className="mr-xs font-bold uppercase">
        {entries.length} {pluralize(['Entry', 'Entries'], entries.length)}
      </span>

      <span className="text-color-subtle">
        <DateTime
          timestamp={startTimestamp}
          toggleRelativeOnClick={false}
          mode="time"
        />

        {entries.length > 1 && startTimestamp !== endTimestamp && (
          <>
            {' - '}
            <DateTime
              timestamp={endTimestamp}
              toggleRelativeOnClick={false}
              mode="time"
            />
          </>
        )}
      </span>
    </span>
  )
}

interface EntriesDayProps {
  date: string
  entries: RPCDocs.Locations.GetLocationLogEntryList.v2.Entry[]
  onDownloadPDF?: (
    entry: RPCDocs.Locations.GetLocationLogEntryList.v2.Entry,
  ) => void
  onEditEntryId?: (entryId: string) => void
}

function EntriesDay({
  date,
  entries,
  onDownloadPDF,
  onEditEntryId,
}: EntriesDayProps) {
  const relativeDate = useMemo(() => getFormattedRelativeDate(date), [date])

  return (
    <div className="space-y-sm">
      <div className="-top-base z-sticky mb-sm border-border bg-background p-xs pt-base sticky flex flex-wrap items-end justify-between border-b">
        <span className="font-medium tracking-wide">{relativeDate}</span>

        <HeaderTimeLabel entries={entries} />
      </div>

      {entries.map((entry) => (
        <EntryListItem
          entry={entry}
          key={entry.id}
          onDownloadPDF={onDownloadPDF}
          onEditEntryId={onEditEntryId}
        />
      ))}
    </div>
  )
}

export {EntriesDay}
